import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useSpeechRecognition — thin wrapper around the browser Web Speech API.
 *
 * Zero cost (no server keys). Works in Chrome, Edge, and Android WebView.
 * Not supported in Safari desktop / Firefox — the hook returns
 * `supported: false` so the UI can hide the mic button gracefully.
 *
 * Usage:
 *   const { supported, listening, start, stop, transcript, activeField } = useSpeechRecognition({
 *     lang: 'en-IN',
 *     onFinal: (text, fieldKey) => setForm(f => ({ ...f, [fieldKey]: (f[fieldKey] + ' ' + text).trim() })),
 *   });
 *   <button onClick={() => start('customer_name')} disabled={!supported}>🎤</button>
 */
export default function useSpeechRecognition({ lang = 'en-IN', onFinal } = {}) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [activeField, setActiveField] = useState(null);
  const recRef = useRef(null);
  const activeFieldRef = useRef(null);
  const onFinalRef = useRef(onFinal);

  useEffect(() => { onFinalRef.current = onFinal; }, [onFinal]);

  const Ctor =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!Ctor;

  useEffect(() => {
    if (!supported) return undefined;
    const r = new Ctor();
    r.lang = lang;
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (ev) => {
      let interim = '';
      let finalText = '';
      for (let i = ev.resultIndex; i < ev.results.length; i += 1) {
        const chunk = ev.results[i][0].transcript;
        if (ev.results[i].isFinal) finalText += chunk;
        else interim += chunk;
      }
      setTranscript(interim || finalText);
      if (finalText && onFinalRef.current) {
        onFinalRef.current(finalText.trim(), activeFieldRef.current);
      }
    };
    r.onend = () => {
      setListening(false);
      setActiveField(null);
      activeFieldRef.current = null;
      setTranscript('');
    };
    r.onerror = () => {
      setListening(false);
      setActiveField(null);
      activeFieldRef.current = null;
    };
    recRef.current = r;
    return () => {
      try { r.stop(); } catch (_) { /* noop */ }
      recRef.current = null;
    };
  }, [Ctor, lang, supported]);

  const start = useCallback((fieldKey) => {
    if (!recRef.current) return;
    // Restart cleanly if already running
    try { recRef.current.stop(); } catch (_) { /* noop */ }
    activeFieldRef.current = fieldKey || null;
    setActiveField(fieldKey || null);
    setTranscript('');
    try {
      recRef.current.start();
      setListening(true);
    } catch (_) {
      // start() throws if the recognizer is already running — safe to ignore.
    }
  }, []);

  const stop = useCallback(() => {
    if (!recRef.current) return;
    try { recRef.current.stop(); } catch (_) { /* noop */ }
    setListening(false);
    setActiveField(null);
    activeFieldRef.current = null;
  }, []);

  return { supported, listening, activeField, transcript, start, stop };
}
